import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { differenceInMinutes } from 'date-fns';
import { 
  BarChart3, 
  Clock, 
  CheckCircle, 
  MessageCircle, 
  Tag, 
  TrendingUp,
  Users,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AVAILABLE_TAGS } from './ConversationTags';

interface ConversationStats {
  id: string;
  status: string;
  requires_human: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

interface MessageStats {
  conversation_id: string;
  role: string;
  created_at: string;
}

export const SupportAnalytics = () => {
  const [conversations, setConversations] = useState<ConversationStats[]>([]);
  const [messages, setMessages] = useState<MessageStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      const [convResult, msgResult] = await Promise.all([
        supabase
          .from('support_conversations')
          .select('id, status, requires_human, tags, created_at, updated_at')
          .order('created_at', { ascending: false }),
        supabase
          .from('support_messages')
          .select('conversation_id, role, created_at')
          .order('created_at', { ascending: true })
      ]);

      if (!convResult.error) {
        setConversations((convResult.data || []).map(c => ({
          ...c,
          tags: c.tags || []
        })));
      }
      if (!msgResult.error) {
        setMessages(msgResult.data || []);
      }
      
      setLoading(false);
    };

    fetchData();
  }, []);

  const analytics = useMemo(() => {
    // Total conversations
    const totalConversations = conversations.length;
    
    // Resolution rate (closed / total)
    const closedConversations = conversations.filter(c => c.status === 'closed').length;
    const resolutionRate = totalConversations > 0 
      ? Math.round((closedConversations / totalConversations) * 100) 
      : 0;
    
    // Human escalation rate
    const humanEscalated = conversations.filter(c => c.requires_human).length;
    const escalationRate = totalConversations > 0 
      ? Math.round((humanEscalated / totalConversations) * 100) 
      : 0;
    
    // AI self-serve rate (resolved without human)
    const aiSelfServed = closedConversations - conversations.filter(c => 
      c.status === 'closed' && c.requires_human
    ).length;
    const selfServeRate = closedConversations > 0 
      ? Math.round((aiSelfServed / closedConversations) * 100) 
      : 0;

    // Average response time (time between user message and assistant response)
    const responseTimes: number[] = [];
    const messagesByConv = messages.reduce((acc, msg) => {
      if (!acc[msg.conversation_id]) acc[msg.conversation_id] = [];
      acc[msg.conversation_id].push(msg);
      return acc;
    }, {} as Record<string, MessageStats[]>);

    Object.values(messagesByConv).forEach(convMessages => {
      for (let i = 0; i < convMessages.length - 1; i++) {
        if (convMessages[i].role === 'user' && convMessages[i + 1].role === 'assistant') {
          const diff = differenceInMinutes(
            new Date(convMessages[i + 1].created_at),
            new Date(convMessages[i].created_at)
          );
          if (diff >= 0 && diff < 60) { // Only count reasonable response times
            responseTimes.push(diff);
          }
        }
      }
    });

    const avgResponseTime = responseTimes.length > 0 
      ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length * 10) / 10
      : 0;

    // Tag distribution
    const tagCounts: Record<string, number> = {};
    conversations.forEach(c => {
      (c.tags || []).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });

    // Volume by day (last 7 days)
    const last7Days: Record<string, number> = {};
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const key = format(date, 'MMM d');
      last7Days[key] = 0;
    }
    
    conversations.forEach(c => {
      const dateKey = format(new Date(c.created_at), 'MMM d');
      if (dateKey in last7Days) {
        last7Days[dateKey]++;
      }
    });

    return {
      totalConversations,
      closedConversations,
      resolutionRate,
      escalationRate,
      selfServeRate,
      avgResponseTime,
      tagCounts,
      volumeByDay: last7Days,
      pendingHuman: conversations.filter(c => c.requires_human && c.status !== 'closed').length,
    };
  }, [conversations, messages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const maxVolume = Math.max(...Object.values(analytics.volumeByDay), 1);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Total Conversations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalConversations}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.pendingHuman} awaiting response
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Resolution Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.resolutionRate}%</div>
            <Progress value={analytics.resolutionRate} className="mt-2 h-1.5" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              AI Self-Serve Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{analytics.selfServeRate}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Resolved without human
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Avg Response Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.avgResponseTime < 1 ? '<1' : analytics.avgResponseTime} min
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              AI responses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Volume Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Conversation Volume (Last 7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {Object.entries(analytics.volumeByDay).map(([day, count]) => (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className="w-full bg-primary/80 rounded-t transition-all"
                    style={{ height: `${(count / maxVolume) * 100}%`, minHeight: count > 0 ? '4px' : '0' }}
                  />
                  <span className="text-[10px] text-muted-foreground">{day.split(' ')[1]}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tag Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Conversations by Tag
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.keys(analytics.tagCounts).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tagged conversations yet
                </p>
              ) : (
                AVAILABLE_TAGS.filter(t => analytics.tagCounts[t.value] > 0)
                  .sort((a, b) => (analytics.tagCounts[b.value] || 0) - (analytics.tagCounts[a.value] || 0))
                  .slice(0, 6)
                  .map(tag => (
                    <div key={tag.value} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${tag.color}`}>
                          {tag.label}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{analytics.tagCounts[tag.value]}</span>
                    </div>
                  ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Escalation Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            Human Escalation Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xl font-bold text-amber-600">{analytics.escalationRate}%</div>
              <p className="text-xs text-muted-foreground">Escalation Rate</p>
            </div>
            <div>
              <div className="text-xl font-bold">{analytics.closedConversations}</div>
              <p className="text-xs text-muted-foreground">Total Resolved</p>
            </div>
            <div>
              <div className="text-xl font-bold text-green-600">
                {analytics.totalConversations - analytics.pendingHuman - analytics.closedConversations}
              </div>
              <p className="text-xs text-muted-foreground">Active (AI handling)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
